import { Sidebar, SidebarContent, SidebarPanel, TabContent, TabContentBody } from "@patternfly/react-core";
import { createRef, type FC, type RefObject, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { TabNames } from "../../constants/product";
import ComponentLoader, { EComponentList } from "../general/ComponentLoader/ComponentLoader";
import AIImportModal from "./AIImportModal/AIImportModal";
import ProductDetailSideNav from "./ProductDetailSideNav/ProductDetailSideNav";

const ProductGeneralDetails = () => <ComponentLoader componentName={EComponentList.ProductGeneralDetails} />;
const ProductFeatures = () => <ComponentLoader componentName={EComponentList.ProductFeatures} />;
const ProductQuickStart = () => <ComponentLoader componentName={EComponentList.ProductQuickStart} />;
const ProductLinkedResources = () => <ComponentLoader componentName={EComponentList.ProductLinkedResources} />;
const ProductFAQ = () => <ComponentLoader componentName={EComponentList.ProductFAQ} />;
const ProductSupport = () => <ComponentLoader componentName={EComponentList.ProductSupport} />;
const ProductLegalDetails = () => <ComponentLoader componentName={EComponentList.ProductLegalDetails} />;
const ProductSEO = () => <ComponentLoader componentName={EComponentList.ProductSEO} />;

const ProductContacts = () => <ComponentLoader componentName={EComponentList.ProductContacts} />;
const ProductVersions = () => <ComponentLoader componentName={EComponentList.ProductVersionMatrix} />;

const ProductComponentMap = {
	[TabNames.general.name]: ProductGeneralDetails,
	[TabNames.features.name]: ProductFeatures,
	[TabNames.quickStart.name]: ProductQuickStart,
	[TabNames.linkedResources.name]: ProductLinkedResources,
	[TabNames.faq.name]: ProductFAQ,
	[TabNames.support.name]: ProductSupport,
	[TabNames.contacts.name]: ProductContacts,
	[TabNames.legal.name]: ProductLegalDetails,
	[TabNames.seo.name]: ProductSEO,
	[TabNames.versionMatrix.name]: ProductVersions,
};

const ProductInfo: FC = () => {
	const location = useLocation();
	const contentRefs = useRef<{ [key: number]: RefObject<any> }>({});
	const [activeTabKey, setActiveTabKey] = useState<number>(0);
	const [showAIImportModal, setShowAIImportModal] = useState(false);
	const navList = Object.keys(TabNames).map((item) => ({
		text: (TabNames as any)[item].name,
		path: (TabNames as any)[item].route,
	}));

	useEffect(() => {
		navList.forEach((_, index) => {
			if (!contentRefs.current[index]) {
				contentRefs.current[index] = createRef();
			}
		});
	}, [navList]);

	useEffect(() => {
		const currentIndex = navList.findIndex((item) => location.pathname.includes(item.path));
		if (currentIndex !== -1) {
			setActiveTabKey(currentIndex);
		}
	}, [location.pathname, navList]);

	return (
		<Sidebar hasGutter data-analytics-category="Product Info|Tabs|Product information">
			<SidebarPanel>
				<ProductDetailSideNav
					contentRefs={contentRefs}
					activeTabKey={activeTabKey}
					setActiveTabKey={setActiveTabKey}
					onImportWithAI={() => setShowAIImportModal(true)}
				/>
			</SidebarPanel>
			<AIImportModal isOpen={showAIImportModal} onClose={() => setShowAIImportModal(false)} />
			<SidebarContent>
				{navList.map((item, index) => {
					const Component = ProductComponentMap[item.text];
					return (
						<TabContent
							key={item.text}
							eventKey={index}
							id={`tab-content-${index}`}
							ref={contentRefs.current[index]}
							aria-label={item.text}
							hidden={activeTabKey !== index}
						>
							<TabContentBody>
								<Component />
							</TabContentBody>
						</TabContent>
					);
				})}
			</SidebarContent>
		</Sidebar>
	);
};

export default ProductInfo;
